import type { CustomersReviewsProps, ProductsProps, TrustProps } from '@/components/shared';
import { sharedConfig } from '@/pageConfig/shared';
import aRated from '@/static/assets/reviews/trust-v2/arated.png';
import bCorp from '@/static/assets/reviews/trust-v2/bcrop.png';
import nyse from '@/static/assets/reviews/trust-v2/nyse.png';
import ogImage from '@/static/assets/home/og.png';
import type { BasePageConfig } from '../../BasePageConfig';
import { ExpertsProps } from '@/components/shared/Experts';
import { HeroProps } from '@/components/reviews/Hero';

const textColor = '#7b7b7b';
const currentYear: number = new Date().getFullYear();

export interface Config extends BasePageConfig {
  hero: HeroProps;
  videosDisclosure?: string;
  customersReviews: CustomersReviewsProps;
  trust: TrustProps;
  products: ProductsProps;
  experts: ExpertsProps;
}

export const config: Config = {
  seo: {
    uniqueCanonicalUrl: 'https://www.lemonade.com/reviews',
    title: `Lemonade Insurance Reviews ${currentYear}`,
    description:
      'Discover why customers love Lemonade Insurance coverage and prices! Read real reviews of renters, homeowners, car, pet, and life insurance.',
    ogImage: ogImage.src,
    metaTitle: `Lemonade Insurance Reviews ${currentYear}`,
  },
  layout: { ...sharedConfig['en-US'].layout },
  hero: {
    title: 'Lemonade Insurance\n Reviews',
    subtitle: 'See what renters, homeowners, car, pet, and life \ninsurance customers are saying about Lemonade',
    ctaLabel: 'Check our Prices',
  },
  videosDisclosure: 'These are real customers, and we compensated\nthem as a thank you for the review',
  customersReviews: {
    title: 'A Lovable, Easy-to-Use Digital Experience',
    subtitle: 'See why over 80,000 people have given the Lemonade app a 5-star review',
    showTweets: false,
    darkBackground: true,
    showAppDownloadLinks: true,
  },
  experts: {
    title: 'Backed by Experts',
    subtitle:
      'When it comes to customer service, innovation, \nprice, and more — Lemonade is recognized as a cut above the rest',
  },
  products: {
    title: 'Incredible Prices. Monthly Subscription.<br/> Bundle Discounts.',
    subtitle: 'Amazing savings when you bundle',
    darkBackground: true,
    products: [
      {
        title: 'Renters',
        subtitle: 'Coverage for your stuff, in<br/> and out of your home',
        price: 'From $5/mo',
        product: 'home_us_renters',
        ctaLabelDesktop: 'check our prices',
        ctaLabelMobile: 'check prices',
      },
      {
        title: 'Homeowners',
        subtitle: 'Protection for your home and stuff',
        price: 'From $25/mo',
        product: 'home_us_owners',
        ctaLabelDesktop: 'check our prices',
        ctaLabelMobile: 'check prices',
      },
      {
        title: 'Car',
        subtitle: 'Protect your car, passengers,<br/>and the planet',
        price: 'as low as $30/mo',
        product: 'car',
        ctaLabelDesktop: 'check our prices',
        ctaLabelMobile: 'check prices',
      },
      {
        title: 'Pet',
        subtitle: 'Health insurance for furry<br/>family members',
        price: 'From $10/mo',
        product: 'pet',
        ctaLabelDesktop: 'check our prices',
        ctaLabelMobile: 'check prices',
      },
      {
        title: 'Term Life',
        subtitle: 'Protecting the people<br/>you love',
        price: 'From $8/mo',
        product: 'life',
        ctaLabelDesktop: 'check our prices',
        ctaLabelMobile: 'check prices',
      },
    ],
    trackingLabel: 'home_us',
  },
  trust: {
    bulletsLayout: 'horizontal',
    bullets: [
      {
        name: 'social',
        title: 'Designed for Social Impact',
        text: 'Lemonade Inc. is a Public Benefit Corporation and certified B-Corp. Social impact is part of our legal mission and business model - not just marketing fluff',
        textColor,
        image: {
          src: bCorp,
          width: 104,
          height: 104,
        },
      },
      {
        name: 'nyse',
        title: 'NYSE:LMND',
        text: 'Lemonade is a publicly traded company, listed on the New York Stock Exchange under the LMND stock symbol',
        textColor,
        image: {
          src: nyse,
          width: 104,
          height: 104,
        },
      },
      {
        name: 'rating',
        title: 'A-Rated and Backed by Giants',
        text: `Lemonade is <a href="https://files.lemonade.com/2022+Annual+Statement+with+signed+jurat.pdf" target="_blank"
                rel="noopener noreferrer">A-rated</a>, fully regulated and reinsured by the most trusted names on the planet`,
        textColor,
        image: {
          src: aRated,
          width: 104,
          height: 104,
        },
      },
    ],
  },
};
